import React, { useEffect, useMemo, useState } from "react";

import DropboxQuotation from "../../components/DropboxQuotation";
import Header from "components/Header";

import { formatBytes } from "../../utils/formateBytes";
import { getQuotationFileApi } from "../../features/file/fileActions";

import {
  useGetQuotationsQuery,
  useUpdateQuotationMutation,
  useAddQuotationMutation,
  useDeleteQuotationMutation,
  useLazyGetQuotationFilesQuery,
  useDeleteQuotationFilesMutation,
} from "state/api";

import {
  Backdrop,
  Box,
  Button,
  CircularProgress,
  useTheme,
  Snackbar,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Modal,
  Typography,
} from "@mui/material";

import { createFilterOptions } from "@mui/material/Autocomplete";
import {
  GridRowModes,
  DataGrid,
  GridToolbarContainer,
  GridActionsCellItem,
} from "@mui/x-data-grid";

import {
  Add as AddIcon,
  Save as SaveIcon,
  Close as CancelIcon,
  Edit as EditIcon,
  AttachFile as AttachFileIcon,
  DeleteOutlined as DeleteIcon,
  PreviewOutlined as PreviewOutlinedIcon,
  Class as ViewQuotationIcon,
} from "@mui/icons-material";

import { Alert as MuiAlert, Tooltip } from "@mui/material";

import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import { useTranslation } from "react-i18next";
import * as locales from "@mui/material/locale";
import { createTheme, ThemeProvider } from "@mui/material/styles";

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function EditToolbar(props) {
  const { t, i18n } = useTranslation();
  const { setRows, setRowModesModel, rows } = props;

  const handleAddRecordClick = () => {
    //get the row id

    const newId =
      rows.length === 0
        ? 1
        : Math.max(...rows.map((r) => (r.id ? r.id : 0) * 1)) + 1;
    const id = newId;
    setRows((oldRows) => [
      ...oldRows,
      {
        id: newId,
        title: "",
        description: "",
        remark: "",
        numOfFiles: 0,
        isNew: true,
      },
    ]);
    setRowModesModel((oldModel) => ({
      ...oldModel,
      [id]: { mode: GridRowModes.Edit, fieldToFocus: "title" },
    }));
  };

  return (
    <GridToolbarContainer>
      <Button
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddRecordClick}
      >
        {t("quotations.ADD QUOTATION")}
      </Button>
    </GridToolbarContainer>
  );
}

const Quotations = () => {
  const { t, i18n } = useTranslation();
  const [locale, setLocale] = useState();
  const theme = useTheme();
  const themeWithLocale = useMemo(
    () => createTheme(theme, locales[locale]),
    [locale, theme]
  );

  const getCurrentLocale = () => {
    return t("currentLocale");
  };

  const currentLocale = getCurrentLocale();

  // set locale for datagrid
  useEffect(() => {
    if (currentLocale === "zh-Hant") {
      setLocale("zhHK");
    } else {
      setLocale("enUS");
    }
  }, [currentLocale]);

  //const { data, isLoading } = useGetAllAssetsFromAdminQuery(); // get all quotation
  const { data: quotations, isLoading: isLoadingQuotations } =
    useGetQuotationsQuery(); // get all quotation

  const [selectedQuotationId, setSelectedQuotationId] = useState(null);

  const [rows, setRows] = useState();
  const [rowModesModel, setRowModesModel] = useState({});

  const [previewUrl, setPreviewUrl] = useState("");
  const [showPreview, setShowPreview] = useState(false);

  const handleShowPreview = () => {
    setShowPreview(true);
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const [openConfirmDeleteDialog, setOpenConfirmDeleteDialog] = useState(false);
  const [deleteId, setDeleteId] = useState("");

  const [openConfirmDeleteFileDialog, setOpenConfirmDeleteFileDialog] =
    useState(false);
  const [deleteFileId, setDeleteFileId] = useState("");

  //for snackbar
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  //for file modal
  const [openFileModal, setOpenFileModal] = React.useState(false);
  const handleOpenFileModal = () => setOpenFileModal(true);
  const handleCloseFileModal = () => setOpenFileModal(false);

  const [updateQuotation, { isLoading: isUpdating }] =
    useUpdateQuotationMutation();

  const [addQuotation, { isLoading: isAdding }] = useAddQuotationMutation();

  const [deleteQuotation, { isLoading: isDeleting }] =
    useDeleteQuotationMutation();

  const [deleteQuotationFile, { isLoading: isDeletingFile }] =
    useDeleteQuotationFilesMutation();

  const [triggerFiles, { isLoading: isLoadingFiles, data: resultFiles }] =
    useLazyGetQuotationFilesQuery();

  // file modal
  const fileModalStyle = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 800,
    bgcolor: theme.palette.primary.main,
    border: "0px",
    boxShadow: 24,
    p: 4,
  };

  useEffect(() => {
    //fetch
    if (selectedQuotationId) {
      triggerFiles({ id: selectedQuotationId });
    }
  }, [selectedQuotationId]);

  // update the data grid row, if loaded with new data
  useEffect(() => {
    setRows(quotations);
  }, [quotations]);

  // *** snackbar *** //
  const handleCloseSnackbar = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnackbar(false);
  };

  const handlePreviewFileClick = (id) => async () => {
    try {
      let resp = {};
      resp = await getQuotationFileApi(id, selectedQuotationId);
      // get the file type
      const contentType = resp.headers["content-type"];
      // Check the file type and set the preview URL accordingly
      if (contentType.startsWith("image/")) {
        const imageUrl = URL.createObjectURL(new Blob([resp.data]));
        setPreviewUrl(imageUrl);
        handleShowPreview();
      } else {
        // For documents or videos, open a new tab for previewing/downloading
        window.open(
          URL.createObjectURL(new Blob([resp.data], { type: contentType }))
        );
      }
    } catch (error) {
      setSnackbarMessage("Error:" + error);
      // show snackbar
      setOpenSnackbar(true);
    }
  };

  // *** confirm delete dialog *** //
  const handleCloseConfirmDeleteDialog = () => {
    setDeleteFileId("");
    setOpenConfirmDeleteDialog(false);
  };

  // *** confirm delete File dialog *** //
  const handleDeleteFileClick = (id) => () => {
    // save the row id
    setDeleteFileId(id);
    setOpenConfirmDeleteFileDialog(true);
  };

  // *** confirm delete file dialog *** //
  const handleCloseConfirmDeleteFileDialog = () => {
    setDeleteFileId("");
    setOpenConfirmDeleteFileDialog(false);
  };

  // *** confirm delete file dialog *** //
  const handleSubmitConfirmDeleteFileDialog = async () => {
    try {
      let response = {};
      //updating
      response = await deleteQuotationFile({
        id: selectedQuotationId,
        body: { fileId: deleteFileId },
      }).unwrap();
      // get message
      if (response.message === "Delete Done!") {
        // set the snackbar message
        setSnackbarMessage(t("quotations.snackbar.Record Deleted"));
        // show snackbar
        setOpenSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Error:" + error);
      // show snackbar
      setOpenSnackbar(true);
    }

    setDeleteFileId("");
    setOpenConfirmDeleteFileDialog(false);
  };

  const handleEditClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleDeleteClick = (id) => () => {
    // save the row id
    setDeleteId(id);
    setOpenConfirmDeleteDialog(true);
  };

  const handleManageFilesClick = (id) => () => {
    let selectedRow = rows.find((row) => row.id === id);
    // update the selected transaction id

    setSelectedQuotationId(selectedRow["_id"]);
    //show attache file modal
    handleOpenFileModal();
  };

  const handleViewQuotationClick = (id) => () => {
    let selectedRow = rows.find((row) => row.id === id);
    //const url = "client/quotation/" + selectedRow["path"];
    const url = "qx/" + selectedRow["path"];
    window.open(url, "_blank"); //to open new page
  };

  const handleSubmitConfirmDeleteDialog = async () => {
    let selectedRow = rows.find((row) => row.id === deleteId);
    // // call api to delete the administrator
    try {
      let response = {};
      //updating
      response = await deleteQuotation({
        id: selectedRow["_id"], // actual id
      }).unwrap();
      // get message
      if (response.message === "Delete Done!") {
        // set the snackbar message
        setSnackbarMessage(t("quotations.snackbar.Record Deleted"));
        // show snackbar
        setOpenSnackbar(true);
      }
    } catch (error) {
      // set the snackbar message
      setSnackbarMessage("Error: " + error);
      // show snackbar
      setOpenSnackbar(true);
    }
    //update the display
    setRows(rows.filter((row) => row.id !== deleteId));
    //reset
    setDeleteId("");
    setOpenConfirmDeleteDialog(false);
  };

  const handleSaveClick = (id) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleCancelClick = (id) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });

    const editedRow = rows.find((row) => row.id === id);
    if (editedRow.isNew) {
      setRows(rows.filter((row) => row.id !== id));
    }
  };

  const handleRowModesModelChange = (newRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const handleRowEditStart = (params, event) => {
    event.defaultMuiPrevented = true;
  };
  const handleRowEditStop = (params, event) => {
    event.defaultMuiPrevented = true;
  };
  const handleProcessRowUpdateError = React.useCallback((error) => {}, []);
  const processRowUpdate = async (newRow) => {
    try {
      let response = {};
      if (newRow["isNew"]) {
        //insert new record
        response = await addQuotation({
          data: newRow,
        }).unwrap();
        //get _id
        const new_id = response._id; //quotation _id
        newRow["_id"] = new_id;
      } else {
        //updating
        response = await updateQuotation({
          id: newRow["_id"],
          data: newRow,
        }).unwrap();
      }
      //get response message
      if (
        response.message === "Update Done!" ||
        response.message === "Add Done!" ||
        response.message === "Delete Done!"
      ) {
        // set the snackbar message
        if (response.message === "Update Done!")
          setSnackbarMessage(t("quotations.snackbar.Record Updated"));
        else if (response.message === "Add Done!")
          setSnackbarMessage(t("quotations.snackbar.Record Added"));
        else if (response.message === "Delete Done!")
          setSnackbarMessage(t("quotations.snackbar.Record Deleted"));
        // show snackbar
        setOpenSnackbar(true);
        const updatedRow = { ...newRow, isNew: false };
        setRows(rows.map((row) => (row.id === newRow.id ? updatedRow : row)));
        return updatedRow;
      }
    } catch (error) {
      return null;
    }
  };

  const renderListHeader = () => {
    let renderListHeader = (
      <Box>
        <h4>{t("quotations.modal.List of current files")}</h4>
      </Box>
    );
    if (!isLoadingFiles && resultFiles) {
      renderListHeader = (
        <Box>
          <h4>
            {t("quotations.modal.List of current files")} (
            {t("quotations.modal.Total")} {resultFiles.length}{" "}
            {t("quotations.modal.files")})
          </h4>
        </Box>
      );
    }
    return renderListHeader;
  };
  const renderFileList = () => {
    let renderListItem = <></>;
    if (!isLoadingFiles && resultFiles) {
      renderListItem = resultFiles.map((e, idx) => (
        <ListItem key={e._id}>
          <ListItemText
            primary={`${idx + 1}. ${e.originalname}`}
            secondary={`(File size: ${formatBytes(e.size)} bytes)`}
          />
          <ListItemIcon>
            <IconButton
              edge="end"
              aria-label={t("quotations.modal.preview")}
              onClick={handlePreviewFileClick(e._id)}
            >
              <Tooltip title={t("quotations.modal.preview")}>
                <PreviewOutlinedIcon />
              </Tooltip>
            </IconButton>
          </ListItemIcon>
          <ListItemIcon>
            <IconButton
              edge="end"
              aria-label={t("quotations.modal.delete")}
              onClick={handleDeleteFileClick(e._id)}
            >
              <Tooltip title={t("quotations.modal.delete")}>
                <DeleteIcon />
              </Tooltip>
            </IconButton>
          </ListItemIcon>
        </ListItem>
      ));
    }

    const content = (
      <List style={{ minWidth: 100, maxHeight: 200, overflow: "scroll" }}>
        {renderListItem}
      </List>
    );
    return content;
  };

  const filterOptions = createFilterOptions({
    limit: 500, // only show 500 entries, the rest need to be search
  });

  const columns = [
    { field: "id", headerName: "No", flex: 0.01, editable: false },
    {
      field: "title",
      headerName: t("quotations.header.Title"),
      flex: 0.05,
      editable: true,
      type: "text",
    },
    {
      field: "description",
      headerName: t("quotations.header.Description"),
      flex: 0.05,
      editable: true,
      type: "text",
    },
    {
      field: "remark",
      headerName: t("quotations.header.Remark"),
      flex: 0.05,
      editable: true,
      type: "text",
    },
    // {
    //   field: "path",
    //   headerName: t("quotations.header.Path"),
    //   flex: 0.05,
    //   editable: false,
    //   type: "text",
    // },
    {
      field: "numOfFiles",
      headerName: t("quotations.header.Num of Attached Files"),
      flex: 0.05,
      editable: false,
    },
    {
      field: "actions",
      type: "actions",
      headerName: t("quotations.header.Actions"),
      width: 150,
      cellClassName: "actions",
      getActions: ({ id }) => {
        const isInEditMode = rowModesModel[id]?.mode === GridRowModes.Edit;

        if (isInEditMode && !isUpdating && !isAdding) {
          return [
            <GridActionsCellItem
              icon={<SaveIcon />}
              label="Save"
              onClick={handleSaveClick(id)}
            />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(id)}
              color="inherit"
            />,
          ];
        }

        if (isUpdating || isAdding) {
          return [
            <>
              <CircularProgress size="1.5rem" />
            </>,
          ];
        }

        return [
          <GridActionsCellItem
            icon={
              <Tooltip title={t("quotations.menu.Edit Quotation")}>
                <EditIcon />
              </Tooltip>
            }
            label={t("quotations.menu.Edit Quotation")}
            className="textPrimary"
            onClick={handleEditClick(id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title={t("quotations.menu.Manage Files")}>
                <AttachFileIcon />
              </Tooltip>
            }
            label={t("quotations.menu.Manage Files")}
            className="textPrimary"
            color="inherit"
            onClick={handleManageFilesClick(id)}
          />,
          <GridActionsCellItem
            icon={
              <Tooltip title={t("quotations.menu.View Quotation")}>
                <ViewQuotationIcon />
              </Tooltip>
            }
            label={t("quotations.menu.View Quotation")}
            className="textPrimary"
            color="inherit"
            onClick={handleViewQuotationClick(id)}
          />,

          <GridActionsCellItem
            icon={<DeleteIcon />}
            label={t("quotations.menu.Delete Quotation")}
            onClick={handleDeleteClick(id)}
            showInMenu
          />,
        ];
      },
    },
  ];

  const tableGrid = () => {
    let content;
    content = (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        {/** Confirm Dialog */}
        <Dialog
          open={openConfirmDeleteDialog}
          onClose={handleCloseConfirmDeleteDialog}
        >
          <DialogContent
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
            }}
          >
            <DialogContentText>
              {t("quotations.dialog.Confirm Delete")}
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
            }}
          >
            <Button
              onClick={handleCloseConfirmDeleteDialog}
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: `${theme.palette.secondary[100]} !important`,
              }}
            >
              {t("quotations.dialog.CANCEL")}
            </Button>
            <Button
              onClick={handleSubmitConfirmDeleteDialog}
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: `${theme.palette.secondary[100]} !important`,
              }}
            >
              {t("quotations.dialog.DELETE")}
            </Button>
          </DialogActions>
        </Dialog>
        {/** Snackbar */}
        <Snackbar
          open={openSnackbar}
          autoHideDuration={1000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "center",
          }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            sx={{
              width: "100%",
              backgroundColor: theme.palette.secondary.light,
              color: theme.palette.background.alt,
              fontSize: "14px",
              fontWeight: "bold",
              padding: "10px 20px",
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
        <Dialog
          open={openConfirmDeleteFileDialog}
          onClose={handleCloseConfirmDeleteFileDialog}
        >
          <DialogContent
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
            }}
          >
            <DialogContentText>
              {t("quotations.deleteFileDialog.Confirm Delete")}
            </DialogContentText>
          </DialogContent>
          <DialogActions
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
            }}
          >
            <Button
              onClick={handleCloseConfirmDeleteFileDialog}
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: `${theme.palette.secondary[100]} !important`,
              }}
            >
              {t("quotations.deleteFileDialog.CANCEL")}
            </Button>
            <Button
              onClick={handleSubmitConfirmDeleteFileDialog}
              sx={{
                backgroundColor: theme.palette.background.alt,
                color: `${theme.palette.secondary[100]} !important`,
              }}
            >
              {t("quotations.deleteFileDialog.DELETE")}
            </Button>
          </DialogActions>
        </Dialog>
        <Modal
          open={openFileModal}
          onClose={handleCloseFileModal}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
          style={{ overflow: "scroll" }}
        >
          <Box sx={fileModalStyle}>
            <Backdrop
              open={showPreview}
              onClick={handleClosePreview}
              sx={{ zIndex: 99, width: "100%" }}
            >
              <img src={previewUrl} width="200px" />
            </Backdrop>
            <Typography id="modal-modal-title" variant="h4" component="h2">
              {t("quotations.modal.Manage Files")}
            </Typography>
            {renderListHeader()}
            <Box
              sx={{
                backgroundColor: theme.palette.primary[300],
                color: theme.palette.grey[1],
              }}
            >
              {renderFileList()}
            </Box>

            {isLoadingFiles && (
              <Box>
                <h4>Loading ...</h4>
              </Box>
            )}
            <Typography
              id="modal-modal-description"
              sx={{ mt: 2 }}
            ></Typography>

            <Divider
              variant="middle"
              sx={{
                backgroundColor: theme.palette.primary[200],
                color: theme.palette.grey[0],
              }}
            />
            <Box>
              <h4>{t("quotations.modal.Add Files")}</h4>
            </Box>

            <DropboxQuotation quotationId={selectedQuotationId} />
          </Box>
        </Modal>
        <Box
          mt="40px"
          height="75vh"
          sx={{
            "& .MuiDataGrid-root": {
              border: "none",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "none",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderBottom: "none",
            },
            "& .MuiDataGrid-virtualScroller": {
              backgroundColor: theme.palette.primary.light,
            },
            "& .MuiDataGrid-footerContainer": {
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[100],
              borderTop: "none",
            },
            "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
              color: `${theme.palette.secondary[200]} !important`,
            },
          }}
        >
          <ThemeProvider theme={themeWithLocale}>
            <DataGrid
              loading={isLoadingQuotations || !quotations}
              rows={rows || []}
              columns={columns}
              getRowId={(row) => row.id}
              editMode="row"
              rowModesModel={rowModesModel}
              onRowModesModelChange={handleRowModesModelChange}
              onRowEditStart={handleRowEditStart}
              onRowEditStop={handleRowEditStop}
              processRowUpdate={processRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              slots={{
                toolbar: EditToolbar,
              }}
              slotProps={{
                toolbar: { setRows, setRowModesModel, rows },
              }}
            />
          </ThemeProvider>
        </Box>
      </LocalizationProvider>
    );
    return content;
  };

  return (
    <Box m="1.5rem 2.5rem">
      <Header
        title={t("quotations.QUOTATIONS")}
        subtitle={t("quotations.List of Quotations")}
      />
      {tableGrid()}
    </Box>
  );
};

export default Quotations;
